import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { JsonPanel } from '@components/ui/JsonPanel';
import { webhookEventsAPI, type WebhookEventPayload } from './webhookResource';

/**
 * Raw webhook payload section for the event detail surface (#1388).
 *
 * Mounted as `webhookResource`'s only form-rail tab, so it renders inside
 * `ResourceDetailPanel` on BOTH the drawer and the `/webhooks/:id` page.
 * The payload lives behind a dedicated admin-gated endpoint (never on the
 * list/detail rows), so it auto-loads here with its own query. A retention-
 * thinned event returns `raw_payload: ''` with `thinned: true`; that is a
 * successful read (200), NOT an error, so it gets its own muted message
 * rather than the error branch.
 */
export function WebhookPayloadCard({ eventId }: { eventId: string }) {
  const { t } = useTranslation();
  const query = useQuery<WebhookEventPayload, Error>({
    queryKey: ['webhook-events', 'payload', eventId],
    queryFn: () => webhookEventsAPI.getPayload(eventId),
    staleTime: 30 * 1000,
  });

  return (
    <section className="lg:col-span-2 space-y-1">
      <div className="text-sm font-semibold text-foreground">{t('webhook.payload.title')}</div>
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('webhook.payload.loading')}</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          {t('webhook.payload.loadError', {
            detail: query.error?.message ?? String(query.error),
          })}
        </p>
      ) : query.data?.thinned ? (
        <p className="text-sm text-muted-foreground">{t('webhook.payload.thinned')}</p>
      ) : (
        <JsonPanel
          value={query.data?.raw_payload ?? ''}
          ariaLabel={t('webhook.payload.title')}
          label={t('webhook.payload.title')}
        />
      )}
    </section>
  );
}

export default WebhookPayloadCard;
